import { useEffect, type RefObject } from "react";

/**
 * GH6 — "the header switches to its scrolled background once the user scrolls past it." Watches a
 * zero-height sentinel where the header starts and writes `data-cl-scrolled` on `targetRef`
 * directly: the answer feeds only the stylesheet, so it never costs a render.
 */
export function useStickyScrolled(
  sentinelRef: RefObject<Element | null>,
  targetRef: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    const sentinel = sentinelRef.current;
    /* Guarded because jsdom ships no IntersectionObserver, so a consumer's test would throw on
       mount. Not a browser-support guard. */
    if (!sentinel || typeof IntersectionObserver !== "function") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        const scrolled = entries.some(entry => !entry.isIntersecting);
        targetRef.current?.setAttribute("data-cl-scrolled", scrolled ? "true" : "false");
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sentinelRef, targetRef]);
}
